import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";

// ── Critical components (no lazy load) ───────────────────────
import Header from "./components/Header";
import Footer from "./components/Footer";
import FlightSearchForm from "./components/FlightSearch/FlightSearchForm";
import ScrollToTop from "./components/ScrolltoTop";

// ── Homepage sections ─────────────────────────────────────────
const WhyBookWithUs        = lazy(() => import("./components/WhyBookwithus"));
const BookingProcess       = lazy(() => import("./components/BookingProcess"));
const PopularRoutes        = lazy(() => import("./components/PopularRoutes"));
const FooterTabs           = lazy(() => import("./FooterTabs"));

// ── Legal / Policy pages ──────────────────────────────────────
const PrivacyPolicy            = lazy(() => import("./components/PrivacyPolicy"));
const TermsAndConditions       = lazy(() => import("./components/TermsAndConditions"));
const FulfillmentPolicy        = lazy(() => import("./components/FulfilmentPolicy"));
const CancellationRefundPolicy = lazy(() => import("./components/CancellationRefundPolicy"));
const AdvertiserDisclosure     = lazy(() => import("./components/AdvertiserDisclosure"));
const CookiesPolicy            = lazy(() => import("./components/CookiesPolicy"));
const FareDisclosurePolicy     = lazy(() => import("./components/FareDisclosurePolicy"));
const PriceMatchPromise        = lazy(() => import("./components/PriceMatchPromise"));
const PostTicketingServiceFee  = lazy(() => import("./components/PostTicketingService"));
const TaxesAndFeesTable        = lazy(() => import("./components/TaxesAndFeesTable"));

// ── Info pages ────────────────────────────────────────────────
const AboutUs    = lazy(() => import("./components/Aboutus"));
const ContactUs  = lazy(() => import("./components/Contact"));
const InvoicePage = lazy(() => import("./components/InvoicePage"));
const PaymentPage     = lazy(() => import("./components/PaymentPage"));
const PaymentCallback = lazy(() => import("./components/PaymentCallback"));
const PayLink         = lazy(() => import("./components/PayLink"));

// ── Flight search / results ───────────────────────────────────
const FlightResults       = lazy(() => import("./components/FlightResults/FlightResults"));
const ReturnFlightResults = lazy(() => import("./components/FlightResults/ReturnFlight"));
const FlightForm          = lazy(() => import("./components/FlightForm/FlightForm"));

// ── Popular route pages ───────────────────────────────────────
const NewYorkToLosAngeles    = lazy(() => import("./components/PopularRoutes/NewYorkToLosAngeles"));
const ChicagoToSanFrancisco  = lazy(() => import("./components/PopularRoutes/ChicagoToSanFrancisco"));
const DallasToLasVegas       = lazy(() => import("./components/PopularRoutes/DallasToLasVegas"));
const MiamiToNewYork         = lazy(() => import("./components/PopularRoutes/MiamiToNewYork"));
const OrlandoToAtlanta       = lazy(() => import("./components/PopularRoutes/OrlandoToAtlanta"));
const HoustonToPhoenix       = lazy(() => import("./components/PopularRoutes/HoustonToPhoenix"));
const DestinationPage        = lazy(() => import("./components/DestinationPage"));
const RouteDetails           = lazy(() => import("./components/RouteDetails"));

// ── Call-to-action banners ────────────────────────────────────
const MobileCallBanner = lazy(() => import("./components/CallMobile"));
const CheapFlights     = lazy(() => import("./components/CheapFlights"));
const BottomCallBar    = lazy(() => import("./components/BottomCallBar"));
const TopCallBar       = lazy(() => import("./components/TopCallBar"));

// ── Loading fallback ──────────────────────────────────────────
const LoadingFallback = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "260px",
      flexDirection: "column",
      gap: "14px",
    }}
  >
    <div
      style={{
        width: "36px",
        height: "36px",
        border: "3px solid #e2e8f0",
        borderTop: "3px solid #38bdf8",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }}
    />
    <p style={{ color: "#94a3b8", fontSize: "13px", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
      Loading…
    </p>
  </div>
);

// ── Shared page shell ─────────────────────────────────────────
const Page = ({ children, searchForm = false }) => (
  <div className="bg-slate-50 min-h-screen flex flex-col">
    <Header />
    {searchForm && <FlightSearchForm />}
    <main className="flex-1">
      <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
    </main>
    <Footer />
  </div>
);

function App() {
  const sameRoutes = [
    "/aa", "/dl", "/fr", "/ua", "/mix",
    "/internationl-flight-assistance",
    "/domestic-flight-assistance",
    "/book-flights",
    "/flights-reservations",
    "/domestic-reservations",
    "/international-reservations",
    "/flight-bookings",
    "/airlines",
  ];

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>

          {/* ── Airline / service routes ── */}
          {sameRoutes.map((route) => (
            <Route
              key={route}
              path={route}
              element={
                <div className="bg-slate-50">
                  <div className="mt-12 hidden md:block">
                    <Header />
                  </div>
                  <Suspense fallback={<div style={{ minHeight: "50px" }} />}>
                    <TopCallBar />
                  </Suspense>
                  <div className="mt-5">
                    <Suspense fallback={<div style={{ minHeight: "50px" }} />}>
                      <MobileCallBanner />
                    </Suspense>
                  </div>
                  <FlightSearchForm />
                  <Suspense fallback={<div style={{ minHeight: "200px" }} />}>
                    <CheapFlights />
                  </Suspense>
                  <Suspense fallback={<div style={{ minHeight: "50px" }} />}>
                    <BottomCallBar />
                  </Suspense>
                  <Footer />
                </div>
              }
            />
          ))}

          {/* ── Homepage ── */}
          <Route
            path="/"
            element={
              <div className="bg-slate-50 min-h-screen flex flex-col">
                <Header />
                <FlightSearchForm />
                <main className="flex-1">
                  <Suspense fallback={<LoadingFallback />}>
                    <WhyBookWithUs />
                    <BookingProcess />
                    <PopularRoutes />
                    <FooterTabs />
                  </Suspense>
                </main>
                <Footer />
              </div>
            }
          />

          {/* ── Legal pages ── */}
          <Route path="/terms-and-conditions"      element={<Page><TermsAndConditions /></Page>} />
          <Route path="/privacy-policy"            element={<Page><PrivacyPolicy /></Page>} />
          <Route path="/fulfillment-policy"        element={<Page><FulfillmentPolicy /></Page>} />
          <Route path="/cancellation-refund-policy" element={<Page><CancellationRefundPolicy /></Page>} />
          <Route path="/advertiser-disclosure"     element={<Page><AdvertiserDisclosure /></Page>} />
          <Route path="/cookies-policy"            element={<Page><CookiesPolicy /></Page>} />
          <Route path="/fare-disclosure-policy"    element={<Page><FareDisclosurePolicy /></Page>} />
          <Route path="/price-match-promise"       element={<Page><PriceMatchPromise /></Page>} />
          <Route path="/post-ticketing-service-fee" element={<Page><PostTicketingServiceFee /></Page>} />
          <Route path="/taxes-and-fees-table"      element={<Page><TaxesAndFeesTable /></Page>} />

          {/* ── Info pages ── */}
          <Route path="/invoice"    element={<Page><InvoicePage /></Page>} />
          <Route path="/aboutus"    element={<Page><AboutUs /></Page>} />
          <Route path="/contactus"  element={<Page><ContactUs /></Page>} />

          {/* ── Payment ── */}
          <Route
            path="/payment"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PaymentPage />
              </Suspense>
            }
          />
          <Route
            path="/payment-callback"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PaymentCallback />
              </Suspense>
            }
          />
          {/* Short payment link — customer visits this URL */}
          <Route
            path="/pay/:code"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PayLink />
              </Suspense>
            }
          />

          {/* ── Popular routes ── */}
          <Route path="/new-york-to-los-angeles"   element={<Page searchForm><NewYorkToLosAngeles /></Page>} />
          <Route path="/chicago-to-san-francisco"  element={<Page searchForm><ChicagoToSanFrancisco /></Page>} />
          <Route path="/dallas-to-las-vegas"       element={<Page searchForm><DallasToLasVegas /></Page>} />
          <Route path="/houston-to-phoenix"        element={<Page searchForm><HoustonToPhoenix /></Page>} />
          <Route path="/orlando-to-atlanta"        element={<Page searchForm><OrlandoToAtlanta /></Page>} />
          <Route path="/miami-to-new-york"         element={<Page searchForm><MiamiToNewYork /></Page>} />

          {/* ── Dynamic routes ── */}
          <Route path="/destinations/:citySlug" element={<Page searchForm><DestinationPage /></Page>} />
          <Route path="/routes/:slug"           element={<Page searchForm><RouteDetails /></Page>} />

          {/* ── Flight results ── */}
          <Route
            path="/results"
            element={
              <div className="bg-slate-50 min-h-screen flex flex-col">
                <Header />
                <main className="flex-1">
                  <Suspense fallback={<LoadingFallback />}>
                    <FlightResults />
                  </Suspense>
                </main>
                <Footer />
              </div>
            }
          />

          <Route
            path="/form"
            element={
              <div className="bg-slate-50 min-h-screen flex flex-col">
                <Header />
                <main className="flex-1">
                  <Suspense fallback={<LoadingFallback />}>
                    <FlightForm />
                  </Suspense>
                </main>
                <Footer />
              </div>
            }
          />

          <Route
            path="/returnflight"
            element={
              <div className="bg-slate-50 min-h-screen flex flex-col">
                <Header />
                <main className="flex-1">
                  <Suspense fallback={<LoadingFallback />}>
                    <ReturnFlightResults />
                  </Suspense>
                </main>
                <Footer />
              </div>
            }
          />

        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
